import * as THREE from 'three';
import type { ContactPoolMode } from '../physics/types';

export const WATERFALL_GUTTER_STATIC_BODY_COUNT = 16;

export type WaterfallGutterProductionManifest = {
  dynamicCapacity: number;
  initialDynamicBodyCount: number;
  halfExtents: [number, number, number];
  friction: number;
  grid: {
    x: number;
    z: number;
    startY: number;
    sizeX: number;
    sizeY: number;
    sizeZ: number;
    spacing: number;
    stacksX: number;
    stacksZ: number;
    stackSpacingX: number;
    stackSpacingZ: number;
  };
  maxPairsPerBodyBroadphase: number;
  maxPersistentManifolds: number;
  pairManifoldSlots: number;
  contactPoolMode: ContactPoolMode;
  compactContactPoolCapacity: number;
  maxContactsPerBodySolver: number;
  pairSolveColorCount: number;
  solverIterations: number;
};

/** One authoritative dynamic/capacity manifest shared by production and QA. */
export const WATERFALL_GUTTER_PRODUCTION_MANIFEST: WaterfallGutterProductionManifest = {
  dynamicCapacity: 2048,
  initialDynamicBodyCount: 11 * 28 * 5,
  halfExtents: [0.3, 0.3, 0.3],
  // Keep enough friction for dense contact churn while remaining below the
  // authored downhill gravity component; the old 0.2 profile locked the late
  // pile at the first receiver and stopped exercising the four transfers.
  friction: 0.12,
  grid: {
    x: -7.0,
    z: 0.0,
    startY: 32.0,
    sizeX: 11,
    sizeY: 28,
    sizeZ: 5,
    spacing: 0.06,
    stacksX: 1,
    stacksZ: 1,
    stackSpacingX: 0.0,
    stackSpacingZ: 0.0,
  },
  maxPairsPerBodyBroadphase: 48,
  maxPersistentManifolds: 65536,
  pairManifoldSlots: 8,
  contactPoolMode: 'compact',
  compactContactPoolCapacity: 65536,
  maxContactsPerBodySolver: 128,
  pairSolveColorCount: 8,
  solverIterations: 10,
};

/** Reproduces createBoxSpawner's exact grid index order without scene objects. */
export function createWaterfallGutterInitialPositions(): Float32Array {
  const manifest = WATERFALL_GUTTER_PRODUCTION_MANIFEST;
  const { grid, halfExtents } = manifest;
  const stepX = halfExtents[0] * 2 + grid.spacing;
  const stepY = halfExtents[1] * 2 + grid.spacing;
  const stepZ = halfExtents[2] * 2 + grid.spacing;
  const plane = grid.sizeX * grid.sizeZ;
  const offsetX = (grid.sizeX - 1) * 0.5;
  const offsetZ = (grid.sizeZ - 1) * 0.5;
  const positions = new Float32Array(manifest.initialDynamicBodyCount * 3);
  for (let index = 0; index < manifest.initialDynamicBodyCount; index++) {
    const gy = Math.floor(index / plane);
    const rem = index - gy * plane;
    const gz = Math.floor(rem / grid.sizeX);
    const gx = rem - gz * grid.sizeX;
    const offset = index * 3;
    positions[offset] = grid.x + (gx - offsetX) * stepX;
    positions[offset + 1] = grid.startY + gy * stepY;
    positions[offset + 2] = grid.z + (gz - offsetZ) * stepZ;
  }
  return positions;
}

export type WaterfallGutterStaticBoxOptions = {
  quaternion: [number, number, number, number];
  color: number;
};

export type WaterfallGutterBuildResult = {
  staticBodyCount: number;
  segmentCenters: readonly THREE.Vector3[];
  segmentOrientations: readonly THREE.Quaternion[];
  /** Inner face of the two physical side walls, in segment-local metres. */
  corridorHalfWidth: number;
  /** Physical wall-top envelope above the tilted floor, in local metres. */
  corridorHeight: number;
  exitCenter: THREE.Vector3;
  exitDirection: THREE.Vector3;
};

/**
 * Builds the exact production Waterfall Gutter static collision geometry.
 * Keeping the layout behind a callback lets the interactive demo and the GPU
 * regression harness share one authoritative scene instead of maintaining a
 * smaller approximation with different joins and wall orientations.
 */
export function buildWaterfallGutterDemo(options: {
  addStaticBox: (
    position: [number, number, number],
    halfExtents: [number, number, number],
    boxOptions: WaterfallGutterStaticBoxOptions,
  ) => void;
}): WaterfallGutterBuildResult {
  // A 15.6-degree downhill component keeps a dense AVBD pile moving through
  // all four transfers. The former 10.3-degree floor settled into a correct
  // but static first-receiver heap even with low friction, so the performance
  // scene stopped exercising downstream contact churn after ~100 bodies.
  const gutterTilt = -0.28;
  // The original 6.4 m-wide, 2.2 m-high trough allowed a large fraction of
  // the 1,540-body impact column to jump the walls at every 90-degree landing.
  // Keep the same five floors, ten walls, four free-fall transfers and open
  // outlet, but make each receiver wide/tall enough to test sustained contact
  // churn instead of mostly testing unconstrained ballistic escape.
  const gutterFloorHalfExtents: [number, number, number] = [11.0, 0.45, 7.5];
  const gutterWallHalfExtents: [number, number, number] = [11.0, 4.0, 0.35];
  const gutterWallZ = 7.85;
  // Place the lower wall edge against the tilted floor without burying a
  // large part of the wall in it. The local wall Y axis is tilted by the same
  // angle as the floor.
  const gutterLiftY = gutterWallHalfExtents[1] * Math.cos(Math.abs(gutterTilt)) - 0.03;

  const slopeRatio = Math.tan(Math.abs(gutterTilt));
  const up = new THREE.Vector3(0.0, 1.0, 0.0);
  const makeGutterOrientation = (horizontalFlow: THREE.Vector3): THREE.Quaternion => {
    const flow = horizontalFlow.clone().setY(0).normalize();
    const xAxis = new THREE.Vector3(flow.x, -slopeRatio, flow.z).normalize();
    const zAxis = new THREE.Vector3().crossVectors(up, xAxis).normalize();
    const yAxis = new THREE.Vector3().crossVectors(zAxis, xAxis).normalize();
    return new THREE.Quaternion().setFromRotationMatrix(
      new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis),
    );
  };

  const segmentOrientations = [
    makeGutterOrientation(new THREE.Vector3(1.0, 0.0, 0.0)),
    makeGutterOrientation(new THREE.Vector3(0.0, 0.0, 1.0)),
    makeGutterOrientation(new THREE.Vector3(-1.0, 0.0, 0.0)),
    makeGutterOrientation(new THREE.Vector3(0.0, 0.0, -1.0)),
    makeGutterOrientation(new THREE.Vector3(1.0, 0.0, 0.0)),
  ];

  const segmentCenters: THREE.Vector3[] = [new THREE.Vector3(0.0, 22.0, 0.0)];
  const segmentHalfLength = gutterFloorHalfExtents[0];
  const dropPerJoin = 5.8;
  const joinLocalOffsets: ReadonlyArray<readonly [number, number, number]> = [
    [5.0, dropPerJoin, 3.0],
    [5.0, dropPerJoin, 3.0],
    [5.0, dropPerJoin, 3.0],
    [5.0, dropPerJoin, 3.0],
  ];
  for (let i = 1; i < segmentOrientations.length; i++) {
    const prevCenter = segmentCenters[i - 1]!;
    const prevOrientation = segmentOrientations[i - 1]!;
    const prevDir = new THREE.Vector3(1.0, 0.0, 0.0)
      .applyQuaternion(prevOrientation)
      .normalize();
    const nextDir = new THREE.Vector3(1.0, 0.0, 0.0)
      .applyQuaternion(segmentOrientations[i]!)
      .normalize();
    const prevLowEnd = prevCenter.clone().addScaledVector(prevDir, segmentHalfLength);
    const localOffset = joinLocalOffsets[i - 1] ?? [0.0, -dropPerJoin, 0.0];
    const offsetWorld = new THREE.Vector3(...localOffset).applyQuaternion(prevOrientation);
    const nextHighEnd = prevLowEnd.clone().add(offsetWorld);
    segmentCenters.push(nextHighEnd.addScaledVector(nextDir, segmentHalfLength));
  }

  let staticBodyCount = 0;
  const addGutterSegment = (center: THREE.Vector3, orientation: THREE.Quaternion): void => {
    const q: [number, number, number, number] = [
      orientation.x,
      orientation.y,
      orientation.z,
      orientation.w,
    ];
    options.addStaticBox(
      [center.x, center.y, center.z],
      gutterFloorHalfExtents,
      { quaternion: q, color: 0x4a5563 },
    );
    staticBodyCount++;

    const wallSide = new THREE.Vector3(0.0, 0.0, 1.0)
      .applyQuaternion(orientation)
      .normalize();
    const wallLift = new THREE.Vector3(0.0, gutterLiftY, 0.0);
    const wallA = center.clone().add(wallLift).addScaledVector(wallSide, gutterWallZ);
    const wallB = center.clone().add(wallLift).addScaledVector(wallSide, -gutterWallZ);
    options.addStaticBox(
      [wallA.x, wallA.y, wallA.z],
      gutterWallHalfExtents,
      { quaternion: q, color: 0x6a7689 },
    );
    options.addStaticBox(
      [wallB.x, wallB.y, wallB.z],
      gutterWallHalfExtents,
      { quaternion: q, color: 0x6a7689 },
    );
    staticBodyCount += 2;
  };

  for (let i = 0; i < segmentCenters.length; i++) {
    addGutterSegment(segmentCenters[i]!, segmentOrientations[i]!);
  }

  const firstCenter = segmentCenters[0]!;
  const firstDir = new THREE.Vector3(1.0, 0.0, 0.0)
    .applyQuaternion(segmentOrientations[0]!)
    .normalize();
  const firstHighEnd = firstCenter.clone().addScaledVector(firstDir, -segmentHalfLength);
  const capCenter = firstHighEnd.clone().addScaledVector(firstDir, -0.7);
  const firstQ = segmentOrientations[0]!;
  options.addStaticBox(
    [capCenter.x, capCenter.y, capCenter.z],
    [0.55, 4.4, 7.3],
    {
      quaternion: [firstQ.x, firstQ.y, firstQ.z, firstQ.w],
      color: 0x73839a,
    },
  );
  staticBodyCount++;

  if (staticBodyCount !== WATERFALL_GUTTER_STATIC_BODY_COUNT) {
    throw new Error(
      `[WaterfallGutter] expected ${WATERFALL_GUTTER_STATIC_BODY_COUNT} static bodies, built ${staticBodyCount}.`,
    );
  }

  const lastSegmentCenter = segmentCenters[segmentCenters.length - 1]!.clone();
  const exitDirection = new THREE.Vector3(1.0, 0.0, 0.0)
    .applyQuaternion(segmentOrientations[segmentOrientations.length - 1]!)
    .normalize();
  // This is the physical low-end outlet plane, not the last segment center.
  const exitCenter = lastSegmentCenter.addScaledVector(exitDirection, segmentHalfLength);
  return {
    staticBodyCount,
    segmentCenters,
    segmentOrientations,
    corridorHalfWidth: gutterWallZ - gutterWallHalfExtents[2],
    corridorHeight: gutterWallHalfExtents[1] * 2.0,
    exitCenter,
    exitDirection,
  };
}
